/* eslint-disable jsx-a11y/alt-text */
import styles from './Modal.module.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useContext } from 'react';
import { Authorization, TrelloPowerUpContext } from '@powerupsclub/trello';
import { PaymentsContext } from '@powerupsclub/payments';
import Spinner from '@atlaskit/spinner';
import { Redirect } from 'react-router-dom';
import TextField from '@atlaskit/textfield';

import { TrelloBoard } from '../TrelloBoard';

const SERVER_URL = `${
  process.env.REACT_APP_SERVER_URL || 'http://localhost:3001'
}/archive-manager`;

async function fetchData(idBoard: string, jwt: string) {
  const response = await fetch(`${SERVER_URL}/board/${idBoard}?jwt=${jwt}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response.json();
}

export const Modal = () => {
  const { isLoading: isLoadingTrello, isAuthorized } =
    useContext(TrelloPowerUpContext);
  const { isLoading: isLoadingPayments, shouldShowPaymentModal } =
    useContext(PaymentsContext);

  const isLoading = isLoadingTrello || isLoadingPayments;

  if (isLoading) {
    return (
      <div className={styles.loadingSpinnerContainer}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!isAuthorized) {
    return <Authorization heading="Archive Manager" imageSrc="./logo.png" />;
  }

  if (shouldShowPaymentModal) {
    return (
      <Redirect
        to={{
          pathname: '/payment',
        }}
      />
    );
  }

  return <ArchiveManager />;
};

let lastLoadTime: any;

const ArchiveManager: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [boardData, setBoardData] = useState<any>();
  const [query, setQuery] = useState('');

  const {
    isLoading: isLoadingTrello,
    trelloIframe,
    token,
  } = useContext(TrelloPowerUpContext);

  useEffect(() => {
    trelloIframe.updateModal({
      actions: [
        {
          icon: './loudspeaker.png',
          alt: trelloIframe.localizeKey('Give us feedback'),
          position: 'right',
          callback: (y: any) => {
            y.popup({
              title: y.localizeKey('Give us feedback'),
              url: './feedback',
              height: 350,
            });
          },
        },
      ],
    });
  }, [trelloIframe]);

  const load = useCallback(async () => {
    const board = await trelloIframe.board('id', 'labels');

    const jwt = await trelloIframe.jwt({ state: JSON.stringify({ token }) });

    const _boardData = await fetchData(board.id, jwt);

    setBoardData(_boardData);
    lastLoadTime = new Date().getTime();
    setIsLoading(false);

    const accentColor =
      _boardData?.prefs?.backgroundTopColor ||
      _boardData?.prefs?.backgroundColor;

    if (accentColor) {
      trelloIframe.updateModal({
        accentColor,
      });
    }
  }, [token, trelloIframe]);

  const render = useCallback(async () => {
    const storedLastLoadTime = await trelloIframe.get(
      'board',
      'private',
      'lastLoadTime'
    );

    if (lastLoadTime && storedLastLoadTime > lastLoadTime) {
      load();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, trelloIframe]);

  useEffect(() => {
    if (!isLoadingTrello) {
      load();

      trelloIframe.render(render);
    }
  }, [isLoadingTrello, load, render, token, trelloIframe]);

  if (isLoading || isLoadingTrello) {
    return (
      <div className={styles.loadingSpinnerContainer}>
        <Spinner size="large" />
      </div>
    );
  }

  const finalBoardData = Object.assign({}, boardData);

  if (query.length > 0) {
    const lowerCaseQuery = query.toLowerCase();
    const idListsWithArchivedCards = new Set();
    const idListsWithMatchingName = new Set();

    for (let list of finalBoardData.lists) {
      if (list.name.toLowerCase().includes(lowerCaseQuery)) {
        idListsWithMatchingName.add(list.id);
      }
    }

    finalBoardData.cards = finalBoardData.cards.filter((card: any) => {
      if (idListsWithMatchingName.has(card.idList)) {
        return true;
      }

      return card.name.toLowerCase().includes(lowerCaseQuery);
    });

    for (let card of finalBoardData.cards) {
      idListsWithArchivedCards.add(card.idList);
    }

    finalBoardData.lists = finalBoardData.lists.filter((list: any) => {
      if (idListsWithMatchingName.has(list.id)) {
        return true;
      }

      return list.closed || idListsWithArchivedCards.has(list.id);
    });
  }

  return (
    <div className={styles.modal}>
      {!isLoading && (
        <div className={styles.searchContainer}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            width="100%"
            placeholder="Search archived cards and lists..."
          />
        </div>
      )}
      <TrelloBoard loading={isLoading} boardData={finalBoardData} />
    </div>
  );
};
